import { type Signal, signal } from '@preact/signals-react'
import log from '@susu/headless-commerce/utils/log'
import { isNull } from '@susu/null'
import { isUndefined } from '@susu/undefined'
import type { Configurator } from '../Configurator/Configurator'
import type { Selected } from '../Selection/Selection'
import type { Step } from '../Step/Step'

export type PreloadSearchParams = {
  ids: string | undefined
  step: string | undefined
}

export class Preload {
  selection: Signal<Selected>
  step: Signal<Step['name']['value']>

  constructor(selection: Selected, step: string) {
    this.selection = signal(selection)
    this.step = signal(step)
  }

  static parseSearchParams(searchParams: URLSearchParams): PreloadSearchParams {
    const step = searchParams.get('step')
    const ids = searchParams.get('ids')

    if (isNull(step) || isNull(ids) || step.length === 0 || ids.length === 0) {
      throw new Error('Preload.parseSearchParams: No preload data')
    }

    return {
      step,
      ids,
    }
  }

  static getInstance(
    configurator: Configurator,
    urlSearchParams: URLSearchParams,
  ) {
    try {
      const searchParams = Preload.parseSearchParams(urlSearchParams)

      // Check if the preload data exists.
      if (
        isUndefined(searchParams.step) ||
        isUndefined(searchParams.ids) ||
        searchParams.ids.length === 0
      ) {
        log.info('Preload.getInstance: No preload data, skipping preload')
        return undefined
      }

      const decodedSelection =
        configurator.selection.value.decodeFromQueryString(
          decodeURIComponent(searchParams.ids),
        )

      // Check if the step exists.
      const step = decodeURIComponent(searchParams.step)
      if (step.length === 0) {
        log.error('Preload.getInstance: Step length is 0')
        return undefined
      }

      // Check if the step exists in the configurator.
      const configuratorStep = configurator.findStepByName(step)
      if (isUndefined(configuratorStep)) {
        log.error(`Preload.getInstance: Step not found: ${step}`)
        return undefined
      }

      // Check if each name in the steps exists as a step in the configurator.
      for (const [name] of Object.entries(decodedSelection)) {
        const step = configurator.findStepByName(name)
        if (isUndefined(step)) {
          log.error(`Preload.getInstance: Step not found: ${name}`)
          return undefined
        }
      }

      return new Preload(decodedSelection, step)
    } catch (error: unknown) {
      return undefined
    }
  }
}
